import { isObject } from '@kk/shared/utils/type-guards';
import annualReportsPageRoute from './AnnualReportsPage/route';
import calculatorPageRoute from './CalculatorPage/route';
import contractOverviewPageRoute from './ContractOverviewPage/route';
import contractPageRoute from './ContractPage/route';
import frontPageRoute from './FrontPage/route';
import organizationPageRoute from './OrganizationPage/route';
import profilePageRoute from './ProfilePage/route';
import signatureOverviewPage from './SignatureOverviewPage/route';
import signaturePageRoute from './SignaturePage/route';
import type {
  ExtendedRouteObject,
  HandleObject,
  HandleReturnType,
} from './types';

export const routes = [
  frontPageRoute,
  contractOverviewPageRoute,
  organizationPageRoute,
  contractPageRoute,
  signatureOverviewPage,
  signaturePageRoute,
  calculatorPageRoute,
  profilePageRoute,
  annualReportsPageRoute,
] as const;

function hasNonEmptyProperty(
  value: object,
  property: 'id' | 'path' | 'pathname',
): boolean {
  return property in value && Reflect.get(value, property) !== '';
}

const isRoute = (
  route: unknown,
): route is ExtendedRouteObject<unknown, unknown> => {
  if (!isObject(route)) return false;

  const hasPath =
    hasNonEmptyProperty(route, 'path') ||
    hasNonEmptyProperty(route, 'pathname');

  return hasPath && hasNonEmptyProperty(route, 'id');
};

export const hasHandle = (
  route: unknown,
): route is {
  handle: (handleObject: HandleObject<unknown, unknown>) => HandleReturnType;
} => isRoute(route) && 'handle' in route && typeof route.handle === 'function';

// const routesWithChildren = routes.filter(isRouteWithChildren);

/** for testing purposes */
// const childRoutesWithFullPathsAndParentLoader = routesWithChildren.flatMap(
//   (route) => {
//     return route.children
//       .filter((child) => child.path !== '')
//       .map((child) => ({
//         ...child,
//         path: `${route.path}/${child.path}` as const,
//         loader: route.loader, // inherit loader from parent
//         parent: route,
//       }));
//   },
// );

export const allPaths = [
  ...routes.map((route) => route.path).filter(Boolean),
  // ...childRoutesWithFullPathsAndParentLoader.map((route) => route.path),
] as const;

const allRoutes = [
  ...routes,
  // ...childRoutesWithFullPathsAndParentLoader,
] as const;

const allRouteIds = allRoutes.map((route) => route.id);

export type PathWithPlaceholders = (typeof allPaths)[number];
export type RouteId = (typeof allRouteIds)[number];
export type RouteFromRouteId<Id extends RouteId> = Id extends RouteId
  ? Extract<(typeof allRoutes)[number], { id: Id }>
  : never;
export type LoaderDataFromRouteId<Id extends RouteId> = Id extends RouteId
  ? 'loader' extends keyof RouteFromRouteId<Id>
    ? RouteFromRouteId<Id>['loader'] extends (...args: unknown[]) => infer R
      ? Awaited<R>
      : undefined
    : undefined
  : never;
